import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import materialGroupService from "../../../../../services/web/material-group-service.js";
import SentryRow from "../../../../../components/template/sentry-row.jsx";

const initialFilter = {
    description: "",
}

const MaterialGroupList = () => {
    const navigate = navigateHook();
    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);

    const columnDefs = useMemo(() => [
        {field: "id", hide: true},
        {field: "description", headerName: "Descrição", flex: 1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams, comparator: aggridUtils.lowerUpCaseComparator},
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const { data: groups = [], isLoading } = useQuery({
        queryKey: ['materials-groups'],
        queryFn: async () => {
            const { data } = await materialGroupService.list();
            return data;
        },
    });

    const onChange = useCallback(({ target: { id, value } }) => {
        setFilter(prev => ({ ...prev, [id]: value }));
    }, []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            filterModel[field] = {
                filterType: "text",
                type: "contains",
                filter: value
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    const onRowClicked = ({event, data}) => {
        const id = data.id;
        const urlDev = `/materials/groups/form/${id}`;
        
        let isBlank = false
        if (event.ctrlKey) isBlank = true
        navigate(urlDev, isBlank)
    }

    const openForm = () => {
        const urlDev = `/materials/groups/form`;
        navigate(urlDev)
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Grupos de materiais
                </Template.Title>
                <Button onClick={openForm}>
                    <FaPlus/><span className="font-bold">Adicionar grupo</span>
                </Button>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className={"col-span-12 flex gap-4"}>
                                <InputForm classNameWrapper={"w-full"} id="description" name="description" value={filter.description} onChange={onChange}
                                           label="Descrição"/>

                                <div className="flex justify-start gap-2">
                                    <Button target="primary-outline" onClick={clearFilter} title={"Limpar"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button onClick={search} title={"Buscar"}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={groups}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                onRowClicked={onRowClicked}
                                paginationPageSize={50}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
};

export default MaterialGroupList;